/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { bridgeApi } from "@/lib/bridgeApi";
import { useToast } from "@/hooks/use-toast";
import { usePatientAuth } from "@/contexts/PatientAuthContext";

// --- Batched loader (DataLoader-style) ---------------------------------
// Every ListingCardV2 on a page calls useProviderLikes independently, each
// firing its own get_likes_count request — 8 separate calls for 8 cards on
// the home page alone (confirmed live 2026-08-20). The backend already
// accepts a comma-separated entity_ids list per entity_type in one call
// (bridgeapi/api.php: getLikesCount()) — this collects every card's request
// that fires within the same tick, groups by (entityType, visitorId), and
// fires ONE batched call per group instead of one per card. Same external
// hook signature, so no call site (ListingCardV2 or anywhere else) needs to
// change — a single-category page (e.g. all-Hospital listings) collapses
// from N calls down to exactly 1.
type PendingLikesBatch = {
  ids: Set<string>;
  resolvers: Map<string, (v: { count: number; liked: boolean }) => void>;
  timer: ReturnType<typeof setTimeout> | null;
};
const pendingLikesBatches = new Map<string, PendingLikesBatch>();

function fetchLikesBatched(
  entityType: string,
  providerId: string,
  visitorId: string | undefined,
): Promise<{ count: number; liked: boolean }> {
  const batchKey = `${entityType}|${visitorId ?? ''}`;
  let batch = pendingLikesBatches.get(batchKey);
  if (!batch) {
    batch = { ids: new Set(), resolvers: new Map(), timer: null };
    pendingLikesBatches.set(batchKey, batch);
  }
  batch.ids.add(providerId);

  const resultPromise = new Promise<{ count: number; liked: boolean }>((resolve) => {
    batch!.resolvers.set(providerId, resolve);
  });

  if (!batch.timer) {
    batch.timer = setTimeout(() => {
      pendingLikesBatches.delete(batchKey);
      const ids = Array.from(batch!.ids);
      bridgeApi
        .call<any>('get_likes_count', {
          entity_type: entityType,
          entity_ids: ids.join(','),
          ...(visitorId ? { visitor_id: visitorId } : {}),
        })
        .then((result) => {
          for (const id of ids) {
            const count = result?.data?.[id] ?? result?.[id] ?? 0;
            const liked = result?.liked_by_visitor?.[id] ?? false;
            batch!.resolvers.get(id)?.({ count, liked });
          }
        })
        .catch(() => {
          for (const id of ids) batch!.resolvers.get(id)?.({ count: 0, liked: false });
        });
      // Runs on the next macrotask, not the current one — gives every
      // ListingCardV2 mounting in this same render pass a chance to register
      // its id into `batch.ids` above before the batched call actually fires.
    }, 0);
  }

  return resultPromise;
}

function typeCodeToEntityType(typeCode: number | null | undefined): string {
  switch (typeCode) {
    case 1:  return 'hospital';
    case 2:  return 'doctor';
    case 3:  return 'medical_store';
    case 4:  return 'homecare';
    case 5:  return 'ambulance';
    case 6:  return 'diagnostic';
    case 7:  return 'wholesaler';
    case 8:  return 'blood_bank';
    case 9:  return 'health_camp';
    case 10: return 'ambulance';
    default: return 'provider';
  }
}

export function useProviderLikes(providerId: string | null | undefined, typeCode?: number | null) {
  const entityType = typeCodeToEntityType(typeCode);
  const queryClient = useQueryClient();
  const { patientUser } = usePatientAuth();
  const visitorId = patientUser?.id ? String(patientUser.id) : undefined;
  const [toggling, setToggling] = useState(false);
  const key = ["provider-likes", providerId, entityType, visitorId ?? 'anon'] as const;
  const query = useQuery({
    queryKey: key,
    enabled: !!providerId,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return await fetchLikesBatched(entityType, String(providerId), visitorId);
      } catch {
        return { count: 0, liked: false };
      }
    },
  });

  const { toast } = useToast();

  const toggle = async () => {
    if (!providerId || toggling) return;
    setToggling(true);
    // Optimistic update — instantly red/un-red
    const current = query.data?.liked ?? false;
    queryClient.setQueryData(key, {
      count: (query.data?.count ?? 0) + (current ? -1 : 1),
      liked: !current,
    });
    try {
      const res = await bridgeApi.call<any>('toggle_like', {
            entity_type: entityType,
            entity_id: String(providerId),
            like_action: current ? 'unlike' : 'like',
            ...(visitorId ? { visitor_id: visitorId } : {}),
          });
      queryClient.setQueryData(key, {
        count: res?.total_likes ?? (query.data?.count ?? 0),
        liked: res?.liked ?? !current,
      });
      toast({
        title: !current ? "Saved!" : "Removed",
        description: !current ? "Added to your saved list." : "Removed from saved list.",
      });
    } catch {
      // Rollback on failure
      queryClient.setQueryData(key, { count: query.data?.count ?? 0, liked: current });
      toast({ title: "Error", description: "Could not save. Try again.", variant: "destructive" });
    } finally {
      setToggling(false);
    }
  };

  return {
    count: query.data?.count ?? 0,
    liked: query.data?.liked ?? false,
    isLoading: query.isLoading,
    toggle,
    toggling,
  };
}
